import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Typography,
  Paper,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Card,
  Divider,
} from "@mui/material";
import { styled } from "@mui/system";
import PetsIcon from "@mui/icons-material/Pets";
import { COLORS } from "../utils/COLORS";
import { firestore } from "../firebaseConfig";
import UserContext from "../context/userContext";
import { guidGenerator } from "../utils/HELPER_FUNCTIONS";
import axios from 'axios';
import GlobalStateContext from "../context/globalStateContext";
import { useNavigate } from "react-router-dom";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { storage } from "../firebaseConfig";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import beforeAndAfterImage from "../assets/beforeAndAfter.png";
import * as amplitude from '@amplitude/analytics-browser';
import AuthModal from "../components/AuthModal";

const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const Header = styled(Box)({
  width: "100%",
  padding: "16px 24px",
  borderBottom: "1px solid #374151",
});

const HeaderContent = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
  cursor: "pointer",
});

const BackButton = styled(Button)({
  color: "#9CA3AF",
  fontSize: "14px",
  textTransform: "none",
  "&:hover": {
    color: "#FFFFFF",
    backgroundColor: "transparent",
  },
});

const MainContainer = styled(Box)({
  maxWidth: "600px",
  margin: "0 auto",
  padding: "32px 24px 120px",
  width: "100%",
});

const FormCard = styled(Card)({
  backgroundColor: "#1F2937",
  border: "1px solid #374151",
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "24px",
});

const SectionTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "16px",
});

const ImageUploadBox = styled(Box)({
  border: "2px dashed #374151",
  borderRadius: "12px",
  padding: "32px",
  textAlign: "center",
  backgroundColor: "#111827",
  cursor: "pointer",
  transition: "all 0.2s ease",
  marginBottom: "16px",
  "&:hover": {
    borderColor: "#10B981",
    backgroundColor: "#1F2937",
  },
});

const SubmitButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "16px 32px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  position: "fixed",
  bottom: "24px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "calc(100% - 48px)",
  maxWidth: "552px",
  zIndex: 1000,
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateX(-50%) translateY(-2px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
  "&:disabled": {
    backgroundColor: "#374151",
    color: "#9CA3AF",
  },
});

const Footer = styled(Box)({
  textAlign: "center",
  padding: "32px 24px",
  color: "#6B7280",
  fontSize: "14px",
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
});

const FooterLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const Bullet = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  userSelect: "none",
});

const ageRanges = [
  "0-6 months",
  "6-12 months",
  "1 year old",
  "2 years old",
  "3 years old",
  "4 years old",
  "5 years old",
  "6 years old",
  "7 years old",
  "8 years old",
  "9 years old",
  "10 years old",
  "11 years old",
  "12 years old",
  "13 years old",
  "14 years old",
  "15 years old",
  "16 years old",
  "17 years old",
  "18 years old",
  "19 years old",
  "20 years old",
];

const AddAnimalPage = () => {
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const { user, setUserContext } = useContext(UserContext);
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [animalData, setAnimalData] = useState({
    // Essential fields
    name: "",
    age: "",
    gender: "",
    weight: "",
    breed: "",
    description: "",
    size: "",
    shelterId: "",
    intakeDate: "",
    euthanasiaDate: "",
    reasonForRisk: "Kennel capacity",
    
    // Medical info
    spayedNeutered: false,
    vaccinated: false,
    microchipped: false,
    healthIssues: [],

    // Contact
    contactPhone: "",
    contactName: "",
    
    // Shelter Animal ID
    animalId: "",
    euthanasiaRisk: true,
  });

  const [openShelterModal, setOpenShelterModal] = useState(false);
  const [newShelter, setNewShelter] = useState({ name: "", location: "" });
  const [shelters, setShelters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [urlFetching, setUrlFetching] = useState(false);
  const [fetchUrl, setFetchUrl] = useState("");
  const [textContent, setTextContent] = useState("");

  useEffect(() => {
    const fetchShelters = async () => {
      try {
        const sheltersSnapshot = await firestore.collection("availableShelters").get();
        const shelterList = sheltersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setShelters(shelterList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching shelters:", error);
        setIsLoading(false);
      }
    };

    fetchShelters();
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    setVideos((prevVideos) => [...prevVideos, ...files]);
  };

  const removeImage = (index) => {
    setImages((prevImages) => {
      // Revoke object URL to prevent memory leaks
      const imageToRemove = prevImages[index];
      if (imageToRemove) {
        URL.revokeObjectURL(URL.createObjectURL(imageToRemove));
      }
      return prevImages.filter((_, i) => i !== index);
    });
  };

  const removeVideo = (index) => {
    setVideos((prevVideos) => {
      // Revoke object URL to prevent memory leaks
      const videoToRemove = prevVideos[index];
      if (videoToRemove) {
        URL.revokeObjectURL(URL.createObjectURL(videoToRemove));
      }
      return prevVideos.filter((_, i) => i !== index);
    });
  };

  const handleShelterChange = (event) => {
    const selectedShelterId = event.target.value;
    if (selectedShelterId === "add") {
      setOpenShelterModal(true);
    } else {
      setAnimalData((prevData) => ({
        ...prevData,
        shelterId: selectedShelterId,
      }));
    }
  };

  const handleNewShelterChange = (event) => {
    const { name, value } = event.target;
    setNewShelter((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAnimal = async () => {
    if (!user) {
      setGlobalState({
        ...globalState,
        showAuthModal: true,
      });
      return;
    }

    // Basic form validation
    if (!animalData.animalId || !animalData.name || !animalData.shelterId || !animalData.euthanasiaDate) {
      alert("Please fill out all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const newAnimalId = guidGenerator();
      
      // Generate slug first
      const baseSlug = encodeURIComponent(
        animalData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
      );
      
      // Check if animal ID already exists
      const existingAnimalQuery = await firestore
        .collection("web-available-dogs")
        .where("animalId", "==", animalData.animalId)
        .get();

      if (!existingAnimalQuery.empty) {
        const existingAnimal = existingAnimalQuery.docs[0].data();
        alert(`Animal ID "${animalData.animalId}" (${existingAnimal.name}) has already been posted on PledgeFast`);
        return;
      }

      // Check if slug already exists
      const existingSlugDoc = await firestore
        .collection("web-available-dogs")
        .doc(baseSlug)
        .get();

      let slug = baseSlug;
      if (existingSlugDoc.exists) {
        // Generate unique slug by appending number
        let counter = 1;
        let uniqueSlug = `${baseSlug}-${counter}`;
        
        while (true) {
          const slugCheck = await firestore
            .collection("web-available-dogs")
            .doc(uniqueSlug)
            .get();
          
          if (!slugCheck.exists) {
            slug = uniqueSlug;
            break;
          }
          
          counter++;
          uniqueSlug = `${baseSlug}-${counter}`;
          
          // Safety check to prevent infinite loop
          if (counter > 999) {
            alert(`Unable to create unique URL for "${animalData.name}". Please try a more specific name.`);
            return;
          }
        }
      }

      // Upload images and get their URLs
      const imageUrls = await Promise.all(
        images.map(async (image) => {
          const imageRef = storageRef(storage, `animal_images/${newAnimalId}/${image.name}`);
          await uploadBytes(imageRef, image);
          return await getDownloadURL(imageRef);
        })
      );

      // Upload videos and get their URLs
      const videoUrls = await Promise.all(
        videos.map(async (video) => {
          const videoRef = storageRef(storage, `animal_videos/${newAnimalId}/${video.name}`);
          await uploadBytes(videoRef, video);
          return await getDownloadURL(videoRef);
        })
      );

      const newAnimalData = {
        ...animalData,
        id: newAnimalId,
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
        images: imageUrls,
        videos: videoUrls,
        slug: slug,
      };

      // Save the new dog data to Firestore
      await firestore.collection("web-available-dogs").doc(slug).set(newAnimalData);

      // Clear the form
      setAnimalData({
        name: "",
        age: "",
        gender: "",
        weight: "",
        breed: "",
        description: "",
        size: "",
        shelterId: "",
        intakeDate: "",
        euthanasiaDate: "",
        reasonForRisk: "Kennel capacity",
        spayedNeutered: false,
        vaccinated: false,
        microchipped: false,
        healthIssues: [],
        contactPhone: "",
        contactName: "",
        animalId: "",
        euthanasiaRisk: true,
      });
      setImages([]);
      setVideos([]);

      // Track dog submission event
      amplitude.track('dog_added', {
        dogName: animalData.name,
        dogSlug: slug,
        breed: animalData.breed,
        age: animalData.age,
        shelterId: animalData.shelterId,
        userId: user.uid,
        userEmail: user.email,
        imagesCount: imageUrls.length,
        videosCount: videoUrls.length
      });

      // Show a success message
      alert("Dog added successfully!");

      // Navigate to the new dog's profile page
      navigate(`/${slug}`);
    } catch (error) {
      console.error("Error adding new dog:", error);
      alert("An error occurred while adding the new dog. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddShelter = async () => {
    try {
      const newShelterId = guidGenerator();
      const newShelterObj = {
        id: newShelterId,
        name: newShelter.name,
        location: newShelter.location,
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || "anonymous",
      };

      // Add the new shelter data to Firestore
      await firestore.collection("availableShelters").doc(newShelterId).set(newShelterObj);

      setShelters((prev) => [...prev, newShelterObj]);

      setAnimalData((prevData) => ({
        ...prevData,
        shelterId: newShelterId,
      }));

      // Close the modal
      setOpenShelterModal(false);

      // Clear the new shelter form
      setNewShelter({ name: "", location: "" });

      console.log("New shelter added successfully");
    } catch (error) {
      console.error("Error adding new shelter:", error);
      alert(
        "An error occurred while adding the new shelter. Please try again."
      );
    }
  };

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setAnimalData((prevData) => ({
      ...prevData,
      [name]: event.target.type === "checkbox" ? checked : value,
    }));
  };

  const handleHealthIssuesChange = (event) => {
    const { value } = event.target;
    setAnimalData((prevData) => ({
      ...prevData,
      healthIssues: value.split(",").map((issue) => issue.trim()),
    }));
  };

  const handleUrlFetch = async () => {
    if (!fetchUrl) return;
    
    setUrlFetching(true);
    try {
      let redditResponse;
      
      if (fetchUrl.includes('reddit.com')) {
        const redditJsonUrl = fetchUrl.endsWith('/') ? fetchUrl + '.json' : fetchUrl + '.json';
        
        redditResponse = await axios.get(redditJsonUrl, {
          timeout: 10000
        });
        
        console.log('Raw Reddit data:', redditResponse.data);
        
        // Extract text content from Reddit JSON
        if (Array.isArray(redditResponse.data)) {
          const postData = redditResponse.data[0]?.data?.children?.[0]?.data;
          const commentsData = redditResponse.data[1]?.data?.children || [];
          
          let textContent = '';
          if (postData) {
            textContent += `Title: ${postData.title || ''}\n`;
            textContent += `Post: ${postData.selftext || ''}\n`;
            
            // Add first few comments
            commentsData.slice(0, 3).forEach((comment, index) => {
              if (comment?.data?.body) {
                textContent += `Comment ${index + 1}: ${comment.data.body}\n`;
              }
            });
          }
          
          
          // Call AI function to process the extracted text
          const aiResponse = await axios.post('https://us-central1-pawsai-ecd07.cloudfunctions.net/processTextWithAI', {
            text: textContent
          });
          
          
          if (aiResponse.data.success && aiResponse.data.animalInfo) {
            // Update form fields with AI-extracted data
            setAnimalData(prevData => ({
              ...prevData,
              ...aiResponse.data.animalInfo
            }));
            setFetchUrl(""); // Clear the URL field
          } else {
            alert('Failed to extract dog information from text');
          }
        }
      }
    } catch (error) {
      console.log('Error fetching URL:', error);
      // alert('Error fetching URL content. Please try again.');
    } finally {
      setUrlFetching(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const futureDate = new Date(today.getTime() + (14 * 24 * 60 * 60 * 1000)); // 2 weeks from now
    setAnimalData(prev => ({
      ...prev,
      intakeDate: today.toISOString().split('T')[0],
      euthanasiaDate: futureDate.toISOString().split('T')[0]
    }));
  }, []);

  // Show auth modal on mount if user is not logged in
  useEffect(() => {
    console.log("User context:", user);
    if (!user) {
      setGlobalState(prev => ({
        ...prev,
        showAuthModal: true,
        requireAuth: true // Flag to prevent closing
      }));
    } else {
      setGlobalState(prev => ({
        ...prev,
        showAuthModal: false, // Auto-close when user is loaded
        requireAuth: false // Allow closing when authenticated
      }));
    }
  }, [user, setGlobalState]);

  // Cleanup object URLs on component unmount
  useEffect(() => {
    return () => {
      images.forEach(image => {
        try {
          URL.revokeObjectURL(URL.createObjectURL(image));
        } catch (e) {
          // Ignore errors if URL is already revoked
        }
      });
      videos.forEach(video => {
        try {
          URL.revokeObjectURL(URL.createObjectURL(video));
        } catch (e) {
          // Ignore errors if URL is already revoked
        }
      });
    };
  }, [images, videos]);

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Logo onClick={() => navigate('/')}>
            <PetsIcon />
            PledgeFast
          </Logo>
          <BackButton onClick={() => navigate(-1)}>
            ← Back
          </BackButton>
        </HeaderContent>
      </Header>
      
      <MainContainer>
       
        
        {/* Delightful Header Section */}
        <Box sx={{
          textAlign: "center",
          marginBottom: "40px",
          padding: "32px",
          background: "linear-gradient(145deg, #10B981, #059669, #047857)",
          borderRadius: "20px",
          boxShadow: "0 20px 40px rgba(16, 185, 129, 0.3), 0 0 0 1px rgba(255,255,255,0.1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)",
            pointerEvents: "none"
          }
        }}>
          <Typography sx={{
            fontSize: "32px",
            fontWeight: "900",
            color: "#FFFFFF",
            marginBottom: "8px",
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            position: "relative",
            zIndex: 1
          }}>
            💰 Make Money Saving Dogs
          </Typography>
          
          {/* <Typography sx={{
            fontSize: "20px",
            color: "#F0FDF4",
            lineHeight: "1.5",
            marginBottom: "24px",
            maxWidth: "480px",
            margin: "0 auto 24px",
            fontWeight: "600",
            position: "relative",
            zIndex: 1
          }}>
            Earn 15% of pledges when you POST + SAVE shelter euthanasia dogs
          </Typography> */}
          
          {/* Simple 3-Step Process */}
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            marginTop: "24px",
            position: "relative",
            zIndex: 1,
            maxWidth: "450px",
            margin: "24px auto 0"
          }}>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography sx={{ fontSize: "32px", marginBottom: "8px" }}>📝</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#FFFFFF" }}>
                Post Your Shelter Euthanasia Dogs on PledgeFast
              </Typography>
            </Box>
            
            <Typography sx={{ fontSize: "20px", color: "#FFFFFF", opacity: 0.6 }}>→</Typography>
            
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography sx={{ fontSize: "32px", marginBottom: "8px" }}>🎬</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#FFFFFF" }}>
                Make Viral TikToks That Collect Pledges
              </Typography>
            </Box>
            
            <Typography sx={{ fontSize: "20px", color: "#FFFFFF", opacity: 0.6 }}>→</Typography>
            
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography sx={{ fontSize: "32px", marginBottom: "8px" }}>💰</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#FEF3C7" }}>
                Save a dog + 100% pledges go to rescue in 1 day!
              </Typography>
            </Box>
          </Box>
          
          <Typography sx={{
            fontSize: "16px",
            color: "#F0FDF4",
            marginTop: "20px",
            fontWeight: "500",
            position: "relative",
            zIndex: 1
          }}>
            ✨ All automated using Stripe. ✨
          </Typography>
        </Box>

 <Box sx={{
          display: "flex",
          alignItems: "center",
          margin: "40px 0",
          gap: "16px"
        }}>
          <Divider sx={{ 
            flex: 1, 
            borderColor: "#374151",
            borderWidth: "1px"
          }} />
          <Typography sx={{
            color: "#9CA3AF",
            fontSize: "14px",
            fontWeight: "500",
            textTransform: "uppercase",
            letterSpacing: "1px"
          }}>
            BEFORE VS AFTER. YOUR POST MATTERS.
          </Typography>
          <Divider sx={{ 
            flex: 1, 
            borderColor: "#374151",
            borderWidth: "1px"
          }} />
        </Box>

        {/* Before and After Image */}
        <Box sx={{
          textAlign: "center",
          marginBottom: "32px",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
        }}>
          <img
            src={beforeAndAfterImage}
            alt="Before and After Results"
            style={{
              width: "100%",
              maxWidth: "600px",
              height: "auto",
              display: "block",
              margin: "0 auto"
            }}
          />
        </Box>

        {/* AI Content Upload Section */}
        {/* <FormCard sx={{ marginBottom: "32px" }}>
          <SectionTitle>🤖 Upload Content via AI</SectionTitle>
          <Box sx={{
            border: "2px dashed #059669",
            borderRadius: "16px",
            padding: "24px",
            textAlign: "center",
            backgroundColor: "rgba(16, 185, 129, 0.05)",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#10B981",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
            },
          }}>
            <Typography sx={{ fontSize: "40px", marginBottom: "12px" }}>🚀</Typography>
            <Typography sx={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#10B981",
              marginBottom: "8px"
            }}>
              Drag & Drop Videos, Images, or Text
            </Typography>
            <Typography sx={{
              fontSize: "14px",
              color: "#9CA3AF",
              marginBottom: "16px"
            }}>
              AI will automatically generate dog profiles from your content
            </Typography>
            <Button sx={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              borderRadius: "12px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "600",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}>
              Choose Files or Drop Here
            </Button>
          </Box>
        </FormCard> */}

        {/* Divider */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          margin: "40px 0",
          gap: "16px"
        }}>
          <Divider sx={{ 
            flex: 1, 
            borderColor: "#374151",
            borderWidth: "1px"
          }} />
          <Typography sx={{
            color: "#9CA3AF",
            fontSize: "14px",
            fontWeight: "500",
            textTransform: "uppercase",
            letterSpacing: "1px"
          }}>
            POST A DOG
          </Typography>
          <Divider sx={{ 
            flex: 1, 
            borderColor: "#374151",
            borderWidth: "1px"
          }} />
        </Box>

        {/* AI URL Fetch Section */}
        <FormCard sx={{ marginBottom: "24px" }}>
          <SectionTitle>🤖 Auto-Fill from URL</SectionTitle>
          <Typography sx={{ 
            color: "#9CA3AF", 
            fontSize: "14px", 
            marginBottom: "16px",
            lineHeight: "1.5"
          }}>
            Paste a Reddit post, social media link, or shelter listing URL. AI will extract the dog's information and fill the form fields automatically.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              placeholder="https://www.reddit.com/r/rescuedogs/comments/..."
              value={fetchUrl}
              onChange={(e) => setFetchUrl(e.target.value)}
              disabled={urlFetching}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
            <Button
              onClick={handleUrlFetch}
              disabled={!fetchUrl || urlFetching}
              sx={{
                backgroundColor: "#10B981",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                textTransform: "none",
                minWidth: "120px",
                "&:hover": {
                  backgroundColor: "#059669",
                },
                "&:disabled": {
                  backgroundColor: "#374151",
                  color: "#9CA3AF",
                },
              }}
            >
              {urlFetching ? (
                <CircularProgress size={20} sx={{ color: "#9CA3AF" }} />
              ) : (
                "Fetch Info"
              )}
            </Button>
          </Box>
        </FormCard>

        {/* AI Text Processing Section */}
        <FormCard sx={{ marginBottom: "24px" }}>
          <SectionTitle>🤖 Auto-Fill from Text</SectionTitle>
          <Typography sx={{ 
            color: "#9CA3AF", 
            fontSize: "14px", 
            marginBottom: "16px",
            lineHeight: "1.5"
          }}>
            Paste any text about the dog - social media posts, messages, descriptions. AI will extract the dog's information automatically.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Paste your text here - dog descriptions, social media posts, messages, etc..."
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              disabled={urlFetching}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
            <Button
              onClick={async () => {
                if (!textContent) return;
                
                setUrlFetching(true);
                try {
                  // Call AI function to process the text directly
                  const aiResponse = await axios.post('https://us-central1-pawsai-ecd07.cloudfunctions.net/processTextWithAI', {
                    text: textContent
                  });
                  
                  if (aiResponse.data.success && aiResponse.data.animalInfo) {
                    // Update form fields with AI-extracted data
                    setAnimalData(prevData => ({
                      ...prevData,
                      ...aiResponse.data.animalInfo
                    }));
                    setTextContent(""); // Clear the text field
                  } else {
                    alert('Failed to extract dog information from text');
                  }
                } catch (error) {
                  console.log('Error processing text:', error);
                  alert('Error processing text. Please try again.');
                } finally {
                  setUrlFetching(false);
                }
              }}
              disabled={!textContent || urlFetching}
              sx={{
                backgroundColor: "#10B981",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                textTransform: "none",
                alignSelf: "flex-start",
                "&:hover": {
                  backgroundColor: "#059669",
                },
                "&:disabled": {
                  backgroundColor: "#374151",
                  color: "#9CA3AF",
                },
              }}
            >
              {urlFetching ? (
                <CircularProgress size={20} sx={{ color: "#9CA3AF" }} />
              ) : (
                "Extract Info"
              )}
            </Button>
          </Box>
        </FormCard>

        {/* Media Upload Section */}
        <FormCard>
          <SectionTitle>Photos & Videos</SectionTitle>
          <Box display="flex" gap={2}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="photo-upload"
              multiple
              type="file"
              onChange={handleImageChange}
            />
            <input
              accept="video/*"
              style={{ display: "none" }}
              id="video-upload"
              multiple
              type="file"
              onChange={handleVideoChange}
            />
            <label htmlFor="photo-upload" style={{ flex: 1 }}>
              <Box sx={{
                border: "1px dashed #374151",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
                backgroundColor: "#111827",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#10B981",
                  backgroundColor: "#1F2937",
                },
              }}>
                <AddPhotoAlternateIcon sx={{ fontSize: 24, color: "#6B7280", mb: 1 }} />
                <Typography sx={{ color: "#9CA3AF", fontSize: "12px" }}>
                  Add Photos
                </Typography>
              </Box>
            </label>
            <label htmlFor="video-upload" style={{ flex: 1 }}>
              <Box sx={{
                border: "1px dashed #374151",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
                backgroundColor: "#111827",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#10B981",
                  backgroundColor: "#1F2937",
                },
              }}>
                <VideoLibraryIcon sx={{ fontSize: 24, color: "#6B7280", mb: 1 }} />
                <Typography sx={{ color: "#9CA3AF", fontSize: "12px" }}>
                  Add Videos
                </Typography>
              </Box>
            </label>
          </Box>
          
          {(images.length > 0 || videos.length > 0) && (
            <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
              {images.map((image, index) => (
                <Box key={`img-${index}`} position="relative">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index}`}
                    style={{ 
                      width: 60, 
                      height: 60, 
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #374151"
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(index)}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      backgroundColor: "#EF4444",
                      color: "white",
                      "&:hover": { backgroundColor: "#DC2626" },
                      width: 20,
                      height: 20,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
              {videos.map((video, index) => (
                <Box key={`vid-${index}`} position="relative">
                  <video
                    src={URL.createObjectURL(video)}
                    style={{ 
                      width: 60, 
                      height: 60, 
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #374151"
                    }}
                    muted
                  />
                  <Box sx={{
                    position: "absolute",
                    bottom: 2,
                    left: 2,
                    backgroundColor: "rgba(0,0,0,0.7)",
                    borderRadius: "2px",
                    padding: "1px 3px"
                  }}>
                    <VideoLibraryIcon sx={{ fontSize: 10, color: "white" }} />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeVideo(index)}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      backgroundColor: "#EF4444",
                      color: "white",
                      "&:hover": { backgroundColor: "#DC2626" },
                      width: 20,
                      height: 20,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </FormCard>
        {/* Basic Info */}
        <FormCard>
          <SectionTitle>Basic Information</SectionTitle>
          <Stack spacing={3}>
            <TextField
              required
              fullWidth
              label="Animal ID (from shelter)"
              name="animalId"
              value={animalData.animalId}
              onChange={handleChange}
              placeholder="Enter the shelter's official ID for this animal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#9CA3AF' },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
            
            <TextField
              required
              fullWidth
              label="Dog's Name"
              name="name"
              value={animalData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#9CA3AF' },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
            
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#9CA3AF' }}>Age</InputLabel>
                <Select
                  name="age"
                  value={animalData.age}
                  onChange={handleChange}
                  label="Age"
                  sx={{
                    backgroundColor: '#111827',
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                  }}
                >
                  {ageRanges.map((range) => (
                    <MenuItem key={range} value={range}>
                      {range}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#9CA3AF' }}>Gender</InputLabel>
                <Select
                  name="gender"
                  value={animalData.gender}
                  onChange={handleChange}
                  label="Gender"
                  sx={{
                    backgroundColor: '#111827',
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                  }}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Weight (lbs)"
                name="weight"
                type="number"
                value={animalData.weight}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#111827',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                  '& .MuiInputLabel-root': { color: '#9CA3AF' },
                  '& .MuiInputBase-input': { color: '#FFFFFF' },
                }}
              />
              
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#9CA3AF' }}>Size</InputLabel>
                <Select
                  name="size"
                  value={animalData.size}
                  onChange={handleChange}
                  label="Size"
                  sx={{
                    backgroundColor: '#111827',
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                  }}
                >
                  <MenuItem value="Small">Small</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Large">Large</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              fullWidth
              label="Breed"
              name="breed"
              value={animalData.breed}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#9CA3AF' },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
          </Stack>
        </FormCard>
        {/* Description */}
        <FormCard>
          <SectionTitle>Dog's Story</SectionTitle>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Tell us about this dog's background and personality"
            name="description"
            value={animalData.description}
            onChange={handleChange}
            placeholder="Include any behavioral notes, special needs, or background information..."
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#111827',
                '& fieldset': { borderColor: '#374151' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#9CA3AF' },
              '& .MuiInputBase-input': { color: '#FFFFFF' },
            }}
          />
        </FormCard>
        {/* Shelter & Timeline */}
        <FormCard>
          <SectionTitle>Shelter Information</SectionTitle>
          <Stack spacing={3}>
            <FormControl fullWidth required>
              <InputLabel sx={{ color: '#9CA3AF' }}>Shelter</InputLabel>
              <Select
                name="shelterId"
                value={animalData.shelterId}
                onChange={handleShelterChange}
                label="Shelter"
                sx={{
                  backgroundColor: '#111827',
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                }}
              >
                {shelters.map((shelter) => (
                  <MenuItem key={shelter.id} value={shelter.id}>
                    {shelter.name}
                  </MenuItem>
                ))}
                <MenuItem value="add">
                  <em>+ Add New Shelter</em>
                </MenuItem>
              </Select>
            </FormControl>
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Intake Date"
                name="intakeDate"
                type="date"
                value={animalData.intakeDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#111827',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                  '& .MuiInputLabel-root': { color: '#9CA3AF' },
                  '& .MuiInputBase-input': { color: '#FFFFFF' },
                }}
              />
              
              <TextField
                required
                fullWidth
                label="Euthanasia Date"
                name="euthanasiaDate"
                type="date"
                value={animalData.euthanasiaDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#111827',
                    '& fieldset': { borderColor: '#EF4444' },
                    '&:hover fieldset': { borderColor: '#DC2626' },
                    '&.Mui-focused fieldset': { borderColor: '#EF4444' },
                  },
                  '& .MuiInputLabel-root': { color: '#9CA3AF' },
                  '& .MuiInputBase-input': { color: '#FFFFFF' },
                }}
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#9CA3AF' }}>Reason for Euthanasia Risk</InputLabel>
              <Select
                name="reasonForRisk"
                value={animalData.reasonForRisk}
                onChange={handleChange}
                label="Reason for Euthanasia Risk"
                sx={{
                  backgroundColor: '#111827',
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                }}
              >
                <MenuItem value="Kennel capacity">Kennel capacity</MenuItem>
                <MenuItem value="Behavioral">Behavioral issues</MenuItem>
                <MenuItem value="Medical">Medical issues</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </FormCard>
        {/* Medical & Contact */}
        <FormCard>
          <SectionTitle>Medical & Contact Information</SectionTitle>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Health Issues"
              name="healthIssues"
              value={animalData.healthIssues.join(", ")}
              onChange={handleHealthIssuesChange}
              placeholder="Enter any health issues, separated by commas"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#111827',
                  '& fieldset': { borderColor: '#374151' },
                  '&:hover fieldset': { borderColor: '#10B981' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
                '& .MuiInputLabel-root': { color: '#9CA3AF' },
                '& .MuiInputBase-input': { color: '#FFFFFF' },
              }}
            />
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Contact Name"
                name="contactName"
                value={animalData.contactName}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#111827',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                  '& .MuiInputLabel-root': { color: '#9CA3AF' },
                  '& .MuiInputBase-input': { color: '#FFFFFF' },
                }}
              />
              
              <TextField
                fullWidth
                label="Contact Phone"
                name="contactPhone"
                value={animalData.contactPhone}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#111827',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                  '& .MuiInputLabel-root': { color: '#9CA3AF' },
                  '& .MuiInputBase-input': { color: '#FFFFFF' },
                }}
              />
            </Box>
            
            <Box>
              <Typography sx={{ color: '#9CA3AF', fontSize: '14px', mb: 2 }}>Medical Status</Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={animalData.spayedNeutered}
                      onChange={handleChange}
                      name="spayedNeutered"
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Spayed/Neutered</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={animalData.vaccinated}
                      onChange={handleChange}
                      name="vaccinated"
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Vaccinated</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={animalData.microchipped}
                      onChange={handleChange}
                      name="microchipped"
                      sx={{ color: '#10B981', '&.Mui-checked': { color: '#10B981' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#FFFFFF' }}>Microchipped</Typography>}
                />
              </Stack>
            </Box>
          </Stack>
        </FormCard>

        <SubmitButton
          onClick={() => {
            if (!user) {
              setGlobalState({
                ...globalState,
                showAuthModal: true,
              });
            } else {
              handleAddAnimal();
            }
          }}
          disabled={isLoading || !animalData.animalId || !animalData.name || !animalData.shelterId || !animalData.euthanasiaDate}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: '#FFFFFF' }} />
          ) : (
            "Save Dog Profile"
          )}
        </SubmitButton>
      </MainContainer>

      <Dialog
        open={openShelterModal}
        onClose={() => setOpenShelterModal(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1F2937',
            border: '1px solid #374151',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #374151' }}>
          Add New Shelter
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Shelter Name"
            type="text"
            fullWidth
            value={newShelter.name}
            onChange={handleNewShelterChange}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#111827',
                '& fieldset': { borderColor: '#374151' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#9CA3AF' },
              '& .MuiInputBase-input': { color: '#FFFFFF' },
            }}
          />
          <TextField
            margin="dense"
            name="location"
            label="Shelter Location"
            type="text"
            fullWidth
            value={newShelter.location}
            onChange={handleNewShelterChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#111827',
                '& fieldset': { borderColor: '#374151' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#9CA3AF' },
              '& .MuiInputBase-input': { color: '#FFFFFF' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #374151', pt: 2 }}>
          <Button 
            onClick={() => setOpenShelterModal(false)}
            sx={{ color: '#9CA3AF' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddShelter}
            sx={{ 
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#059669' }
            }}
          >
            Add Shelter
          </Button>
        </DialogActions>
      </Dialog>

      <Footer>
        <FooterLink onClick={() => {
          if (user) {
            navigate(`/profile/${user.uid}`);
          } else {
            setGlobalState({ ...globalState, showAuthModal: true });
          }
        }}>{user ? (user.displayName || user.email?.split('@')[0] || 'Profile') : 'Login'}</FooterLink>
        <Bullet>•</Bullet>
        <FooterLink onClick={() => {
          if (user) {
            navigate('/add-animal');
          } else {
            setGlobalState({ ...globalState, showAuthModal: true });
          }
        }}>Save Your Shelter Dogs</FooterLink>
        <Bullet>•</Bullet>
        <FooterLink onClick={() => navigate('/contact')}>Contact</FooterLink>
      </Footer>

      {/* Auth Modal */}
      {globalState.showAuthModal && <AuthModal />}
    </PageContainer>
  );
};

export default AddAnimalPage;
